// v10/src/components/admin/fuel/DispatchModal.js
//
// Modal "Despachar a aeronave" — registra movement_type='dispatch'.
// Consume /api/aircraft existente (sin modificarlo) para listar aeronaves.

import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Loader, Truck, Plane } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import api from '../../../services/apiService';
import fuelService from '../../../services/fuelService';

const formatNum = (n, locale = 'es-AR') => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString(locale, { maximumFractionDigits: 2 });
};

const DispatchModal = ({ tanks = [], preselectedTankId, onSuccess, onClose, darkMode, locale = 'es-AR' }) => {
  const { t } = useTranslation('admin');

  const activeTanks = useMemo(
    () => tanks.filter((tk) => tk.is_active !== false),
    [tanks]
  );

  const initialTankId = preselectedTankId
    || (activeTanks.length === 1 ? activeTanks[0].id : '');

  const [form, setForm] = useState({
    fuel_tank_id:    initialTankId,
    aircraft_id:     '',
    quantity_liters: '',
    reason:          '',
  });
  const [aircraftList,  setAircraftList]  = useState([]);
  const [loadingAirs,   setLoadingAirs]   = useState(true);
  const [aircraftError, setAircraftError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !loading) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, loading]);

  // Cargar aeronaves activas de la org
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get('/api/aircraft', { params: { is_active: 'true' } });
        const list = res?.data?.data || [];
        if (mounted) {
          setAircraftList(list);
          setLoadingAirs(false);
        }
      } catch (_) {
        if (mounted) {
          setAircraftError(t('fuel_dispatch_modal_err_aircraft_load'));
          setLoadingAirs(false);
        }
      }
    })();
    return () => { mounted = false; };
  }, [t]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const selectedTank = useMemo(
    () => activeTanks.find((tk) => tk.id === form.fuel_tank_id),
    [activeTanks, form.fuel_tank_id]
  );

  const selectedAircraft = useMemo(
    () => aircraftList.find((a) => a.id === form.aircraft_id),
    [aircraftList, form.aircraft_id]
  );

  const validation = useMemo(() => {
    const errors = [];
    if (!form.fuel_tank_id) errors.push(t('fuel_dispatch_modal_err_tank_required'));
    if (!form.aircraft_id)  errors.push(t('fuel_dispatch_modal_err_aircraft_required'));
    const qty = Number(form.quantity_liters);
    if (!Number.isFinite(qty) || qty <= 0) errors.push(t('fuel_dispatch_modal_err_qty_positive'));

    if (selectedTank && Number.isFinite(qty) && qty > 0) {
      const currentStock = Number(selectedTank.current_stock_liters) || 0;
      if (qty > currentStock) {
        errors.push(t('fuel_dispatch_modal_err_exceeds_stock', { stock: formatNum(currentStock, locale) }));
      }
    }
    return { errors, ok: errors.length === 0 };
  }, [form, selectedTank, t, locale]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validation.ok || loading) return;
    setLoading(true); setError('');
    try {
      await fuelService.createMovement({
        fuel_tank_id:    form.fuel_tank_id,
        aircraft_id:     form.aircraft_id,
        movement_type:   'dispatch',
        quantity_liters: Number(form.quantity_liters),
        reason:          form.reason.trim() || null,
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || t('fuel_validation_network_error'));
      setLoading(false);
    }
  };

  // Estilos compartidos
  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.45)' : '#64748b';
  const fgFaint   = darkMode ? 'rgba(255,255,255,0.30)' : '#94a3b8';
  const labelStyle = {
    margin: '0 0 6px', fontSize: 12, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.06em', color: fgFaint,
  };
  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 12,
    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0',
    background: darkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
    color: fgPrimary, fontSize: 14, fontFamily: 'inherit',
    outline: 'none', boxSizing: 'border-box',
  };

  const aircraftCapacity = Number(selectedAircraft?.fuel_capacity_liters);
  const showCapacityHint = Number.isFinite(aircraftCapacity) && aircraftCapacity > 0;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
      }}
      onClick={loading ? undefined : onClose}
    >
      <motion.div
        initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }}
        style={{
          width: '100%', maxWidth: 560, borderRadius: 24,
          background: darkMode ? 'rgba(15,23,42,0.98)' : 'white',
          border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
          padding: 28, boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
          maxHeight: '90vh', overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 40, height: 40, borderRadius: 12,
                background: 'linear-gradient(135deg,#dc2626,#ef4444)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 6px 16px rgba(220,38,38,0.3)',
              }}
            >
              <Truck style={{ width: 20, height: 20, color: 'white' }} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: fgPrimary }}>
                {t('fuel_dispatch_modal_title')}
              </h3>
              <p style={{ margin: '3px 0 0', fontSize: 13, color: fgMuted }}>
                {t('fuel_dispatch_modal_subtitle')}
              </p>
            </div>
          </div>
          <button
            type="button" onClick={onClose} disabled={loading}
            aria-label="close"
            style={{
              padding: 8, borderRadius: 10, border: 'none',
              background: 'transparent', cursor: loading ? 'not-allowed' : 'pointer',
              color: fgMuted,
            }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Tanque */}
          <div style={{ marginBottom: 14 }}>
            <p style={labelStyle}>{t('fuel_dispatch_modal_label_tank')}</p>
            <select
              value={form.fuel_tank_id}
              onChange={(e) => setField('fuel_tank_id', e.target.value)}
              style={{ ...inputStyle, cursor: 'pointer', appearance: 'auto' }}
            >
              <option value="">{t('fuel_dispatch_modal_placeholder_tank')}</option>
              {activeTanks.map((tk) => (
                <option key={tk.id} value={tk.id}>
                  {tk.name} · {tk.fuel_type} · {formatNum(tk.current_stock_liters, locale)}L
                </option>
              ))}
            </select>
          </div>

          {/* Aeronave */}
          <div style={{ marginBottom: 14 }}>
            <p style={labelStyle}>{t('fuel_dispatch_modal_label_aircraft')}</p>
            {loadingAirs ? (
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '10px 14px', color: fgMuted, fontSize: 13,
                }}
              >
                <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
                <Loader style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
                {t('fuel_dispatch_modal_loading_aircraft')}
              </div>
            ) : aircraftError ? (
              <p style={{ margin: 0, fontSize: 13, color: '#ef4444' }}>{aircraftError}</p>
            ) : (
              <>
                <select
                  value={form.aircraft_id}
                  onChange={(e) => setField('aircraft_id', e.target.value)}
                  style={{ ...inputStyle, cursor: 'pointer', appearance: 'auto' }}
                >
                  <option value="">{t('fuel_dispatch_modal_placeholder_aircraft')}</option>
                  {aircraftList.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.registration ? `${a.registration} · ` : ''}{a.manufacturer} {a.model_name}
                    </option>
                  ))}
                </select>
                {showCapacityHint && (
                  <p
                    style={{
                      margin: '6px 0 0', fontSize: 11, color: fgFaint,
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    <Plane style={{ width: 12, height: 12 }} />
                    {t('fuel_dispatch_modal_hint_aircraft_capacity', {
                      capacity: formatNum(aircraftCapacity, locale),
                    })}
                  </p>
                )}
              </>
            )}
          </div>

          {/* Cantidad */}
          <div style={{ marginBottom: 14 }}>
            <p style={labelStyle}>{t('fuel_dispatch_modal_label_qty')}</p>
            <input
              type="number" min="0" step="0.01" style={inputStyle}
              value={form.quantity_liters}
              onChange={(e) => setField('quantity_liters', e.target.value)}
              placeholder="45"
            />
          </div>

          {/* Reason */}
          <div style={{ marginBottom: 16 }}>
            <p style={labelStyle}>{t('fuel_dispatch_modal_label_reason')}</p>
            <textarea
              rows={2} style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
              value={form.reason}
              onChange={(e) => setField('reason', e.target.value)}
              placeholder={t('fuel_dispatch_modal_placeholder_reason')}
            />
          </div>

          {validation.errors.length > 0 && (
            <div
              style={{
                padding: '10px 14px', borderRadius: 12,
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.2)',
                marginBottom: 12,
              }}
            >
              {validation.errors.map((e, i) => (
                <p key={i} style={{ margin: i === 0 ? 0 : '4px 0 0', fontSize: 12, color: '#ef4444', fontWeight: 600 }}>
                  • {e}
                </p>
              ))}
            </div>
          )}

          {error && (
            <div
              style={{
                padding: '10px 14px', borderRadius: 12,
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.2)',
                marginBottom: 12,
              }}
            >
              <p style={{ margin: 0, fontSize: 13, color: '#ef4444', fontWeight: 600 }}>{error}</p>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <motion.button
              type="submit" disabled={loading || !validation.ok}
              whileHover={!loading && validation.ok ? { scale: 1.02 } : {}}
              whileTap={!loading && validation.ok ? { scale: 0.97 } : {}}
              style={{
                flex: 1, padding: 13, borderRadius: 14, border: 'none',
                cursor: loading || !validation.ok ? 'not-allowed' : 'pointer',
                background: loading || !validation.ok
                  ? (darkMode ? 'rgba(255,255,255,0.08)' : '#cbd5e1')
                  : 'linear-gradient(135deg,#dc2626,#ef4444)',
                color: 'white', fontWeight: 700, fontSize: 14, fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              {loading
                ? <Loader style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
                : <Truck style={{ width: 16, height: 16 }} />
              }
              {loading ? t('fuel_dispatch_modal_btn_saving') : t('fuel_dispatch_modal_btn_submit')}
            </motion.button>
            <button
              type="button" onClick={onClose} disabled={loading}
              style={{
                padding: '13px 18px', borderRadius: 14,
                border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                background: 'transparent',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: 600, fontSize: 14, color: fgMuted,
                fontFamily: 'inherit',
              }}
            >
              {t('fuel_modal_btn_cancel')}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default DispatchModal;
